import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { PROJECTS } from "@/constants/testIds";
import { toast } from "sonner";
import { Rocket, Send, Trophy, ArrowLeft, Sparkles, CheckCircle2 } from "lucide-react";

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [paths, setPaths] = useState([]);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/paths").then((r) => setPaths(r.data));
  }, []);

  const generate = async (slug) => {
    setBusy(slug);
    try {
      const { data } = await api.post("/projects/generate", { path_slug: slug });
      toast.success("Project generated!");
      navigate(`/app/projects/${data.project_id}`);
    } catch { toast.error("Generation failed"); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid={PROJECTS.root} className="space-y-8 animate-fade-up">
      <div>
        <div className="label-mono text-primary">// AI project builder</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">
          Build things. That's how you learn.
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Every path deserves a capstone. Your mentor guides you step by step and scores your work.</p>
      </div>

      {projects.length > 0 && (
        <section>
          <div className="label-mono mb-3">// your projects</div>
          <div className="grid gap-4 md:grid-cols-2">
            {projects.map((p) => (
              <Link
                key={p.project_id}
                data-testid={PROJECTS.card(p.project_id)}
                to={`/app/projects/${p.project_id}`}
                className="card-hover rounded-2xl border border-border bg-card p-5"
              >
                <div className="flex items-center justify-between">
                  <div className="label-mono">{p.path_title}</div>
                  {p.status === "completed" ? (
                    <span className="rounded-full border border-accent/40 bg-accent/10 px-2.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-widest text-accent">completed</span>
                  ) : (
                    <span className="rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-widest text-primary">in progress</span>
                  )}
                </div>
                <div className="mt-3 font-display text-lg font-bold">{p.spec?.title || "Untitled project"}</div>
                <div className="mt-1 line-clamp-2 text-sm text-muted-foreground">{p.spec?.description}</div>
                {p.review?.total !== undefined && (
                  <div className="mt-3">
                    <div className="label-mono">Score</div>
                    <div className="mt-1 font-display text-2xl font-black">{p.review.total}/100</div>
                  </div>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="label-mono mb-3">// start a new project</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {paths.slice(0, 9).map((p) => (
            <div key={p.slug} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-lg font-mono text-xs" style={{ backgroundColor: `${p.color}22`, color: p.color, border: `1px solid ${p.color}55` }}>
                  {p.title[0]}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{p.title}</div>
                  <div className="label-mono truncate">{p.category}</div>
                </div>
              </div>
              <Button
                data-testid={PROJECTS.generateBtn(p.slug)}
                onClick={() => generate(p.slug)}
                disabled={busy === p.slug}
                className="mt-3 w-full rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
                size="sm"
              >
                <Rocket className="mr-2 h-3.5 w-3.5" /> {busy === p.slug ? "Generating…" : "Generate project"}
              </Button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
